import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    InputAdornment,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableRow,
    TextField,
    Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import TableViewIcon from '@mui/icons-material/TableView';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import { DataGridPro, GRID_DETAIL_PANEL_TOGGLE_COL_DEF, GridDetailPanelToggleCell } from '@mui/x-data-grid-pro';

import { exportCsv, exportXlsx } from './exportReportRows';

// ─────────────────────────────────────────────────────────────────────────────
// "Voorraad per maat" & friends are FastReport cross-tabs whose size cells are
// filled by report script (empty in the template) — unparseable statically. But
// the SQL returns clean long-format rows (one per article × size cell), so we
// pivot them here: the report's normal grouping/subtotals on the main grid, with
// each article (and each subtotal/total) row carrying an expandable size-table
// subrow (measures × sizes), expanded by default.
// ─────────────────────────────────────────────────────────────────────────────

const MEASURE_DEFS = [
    { field: 'best', key: 'reports_meas_best', label: 'Besteld' },
    { field: 'gele', key: 'reports_meas_gele', label: 'Geleverd' },
    { field: 'verk', key: 'reports_meas_verk', label: 'Verkocht' },
    { field: 'sver', key: 'reports_meas_sver', label: 'Verkocht (solden)' },
    { field: 'voor', key: 'reports_meas_voor', label: 'Voorraad' },
    { field: 'back', key: 'reports_meas_back', label: 'Backorder' },
];
const MEASURE_FIELDS = MEASURE_DEFS.map(m => m.field);

// Article-level columns shown on the main grid (the measures live in the size-table subrow,
// matching the original report). `levNaam` is intentionally absent — it's the group header.
const DESC_FIELDS = [
    { field: 'barbodyKleurnr', label: 'BarbodyK' },
    { field: 'levart1',  label: 'Artikelnr' },
    { field: 'kleurlev', label: 'Kleur lev' },
    { field: 'oms1',     label: 'Omschrijving' },
    { field: 'c1oms',    label: 'Geslacht' },
    { field: 'c2oms',    label: 'Artikelgroep' },
    { field: 'c3oms',    label: 'Subgroep' },
    { field: 'ap',       label: 'ap', money: true },
    { field: 'vp',       label: 'vp', money: true },
];

const ARTICLE_IDENTITY_FIELDS = ['barbodykleurnr', 'barbody_kleurnr_mtposy', 'barbody'];

const num = (v) => {
    if (v === null || v === undefined || v === '') return 0;
    if (typeof v === 'number') return v;
    const n = Number(String(v).replace(',', '.'));
    return Number.isFinite(n) ? n : 0;
};
const clean = (v) => (v === null || v === undefined) ? '' : String(v).trim();
const fmtInt = (n) => (!n ? '' : n.toLocaleString('fr-BE', { maximumFractionDigits: 0 }));

const resolver = (columns) => {
    const map = new Map();
    columns.forEach(c => map.set(String(c).toLowerCase(), c));
    return (name) => map.get(String(name).toLowerCase()) || null;
};

export const isSizeMatrix = (columns = []) => {
    const lc = (columns || []).map(c => String(c).toLowerCase());
    const hasSize = lc.includes('mtposx') || lc.includes('mtomsx');
    const hasMeasure = MEASURE_FIELDS.some(m => lc.includes(m));
    return hasSize && hasMeasure;
};

const buildConfig = (columns, layout, t) => {
    const R = resolver(columns);
    const tx = (key, fallback) => { const v = t ? t(key) : key; return (!v || v === key) ? fallback : v; };

    const measures = MEASURE_DEFS
        .map(m => ({ base: m.field, field: R(m.field), artField: R('art_' + m.field), label: tx(m.key, m.label) }))
        .filter(m => m.field);
    const descCols = DESC_FIELDS
        .map(d => ({ field: R(d.field), label: d.label, money: !!d.money }))
        .filter(d => d.field);

    const mtposX = R('mtposX') || R('mtomsX');
    const mtomsX = R('mtomsX') || R('mtposX');
    const mtposY = R('mtposY');
    const mtomsY = R('mtomsY');

    const articleField = ARTICLE_IDENTITY_FIELDS.map(f => R(f)).find(Boolean);
    const articleKey = articleField ? [articleField] : descCols.map(d => d.field);

    // Grouping: from the FR3 layout, minus the article-identity level (that's the row, not a group)
    // and any size mechanics (already dropped by the parser). Fall back to supplier when present.
    let groups = [];
    const layoutGroups = (layout && Array.isArray(layout.groups)) ? layout.groups : [];
    layoutGroups.forEach(g => {
        const key = R(g.key);
        if (!key) return;
        if (ARTICLE_IDENTITY_FIELDS.includes(String(g.key).toLowerCase())) return;
        const labelField = g.labelField ? (R(g.labelField) || key) : key;
        groups.push({ key, labelField });
    });
    if (groups.length === 0 && R('levnr')) {
        groups.push({ key: R('levnr'), labelField: R('levNaam') || R('levnr') });
    }

    return { measures, descCols, mtposX, mtomsX, mtposY, mtomsY, articleKey, groups };
};

const sizeFieldId = (label) => `s__${label}`;

// Fixed width for the size-table's first column (Y-size header + measure labels) so every
// size table lines up. Wide enough for the longest label ("Verkocht (solden)") plus margin.
const FIRST_COL_SX = { width: 165, minWidth: 165, maxWidth: 165, whiteSpace: 'nowrap' };

// Build aggregated articles, the size axes, and the grouped display rows.
const buildModel = (columns, rows, cfg, searchTerm, showSubtotals) => {
    const { measures, descCols, mtposX, mtomsX, mtposY, mtomsY, articleKey, groups } = cfg;

    // Global ordering maps. Each article has its OWN maatbalk (its own set of sizes, and some
    // have a length/Y axis while others don't), so these maps are used only to order the
    // per-row size tables consistently — the tables themselves are built per row from its cells.
    const sizeMap = new Map();
    const yMap = new Map();
    for (const r of rows) {
        const xl = clean(r[mtomsX]);
        if (xl !== '') { const p = num(r[mtposX]); if (!sizeMap.has(xl) || p < sizeMap.get(xl)) sizeMap.set(xl, p); }
        if (mtomsY) { const yl = clean(r[mtomsY]); if (yl !== '') { const p = num(r[mtposY]); if (!yMap.has(yl) || p < yMap.get(yl)) yMap.set(yl, p); } }
    }
    const globalSizes = [...sizeMap.entries()].sort((a, b) => a[1] - b[1]).map(e => e[0]);
    const globalHasY = yMap.size > 0;

    const cellKey = (y, field, size) => `${y}||${field}||${size}`;

    // Aggregate to one record per article.
    const articles = new Map();
    let order = 0;
    for (const r of rows) {
        const key = articleKey.map(c => clean(r[c])).join('||');
        if (!articles.has(key)) {
            articles.set(key, {
                order: order++,
                desc: descCols.reduce((o, d) => { o[d.field] = clean(r[d.field]); return o; }, {}),
                groupVals: groups.map(g => clean(r[g.key])),
                groupLabels: groups.map(g => clean(r[g.labelField]) || clean(r[g.key])),
                cells: {},     // per-size cell sums
                cellSum: {},   // per-measure sum of cells
                artVal: {},    // article-level total (art_*), constant per article
                hasArt: {},
                xstruct: new Map(), // mtposX -> mtomsX (full maatbalk, regardless of value)
                ystruct: new Map(), // mtposY -> mtomsY
            });
        }
        const a = articles.get(key);
        const y = clean(r[mtomsY]);   // per-article length label (may be empty)
        const size = clean(r[mtomsX]);
        // Capture the article's size structure from ALL its rows so same-maatbalk articles
        // share a signature (used to keep subtotal/total size tables separate per maatbalk).
        if (size !== '') a.xstruct.set(clean(r[mtposX]), size);
        a.ystruct.set(clean(r[mtposY]), y);
        for (const m of measures) {
            const v = num(r[m.field]);
            if (v) {
                a.cells[cellKey(y, m.field, size)] = (a.cells[cellKey(y, m.field, size)] || 0) + v;
                a.cellSum[m.field] = (a.cellSum[m.field] || 0) + v;
            }
            if (m.artField) { a.artVal[m.field] = num(r[m.artField]); a.hasArt[m.field] = true; }
        }
    }

    // Finalise per-article totals: prefer the report's article-level total (art_*) when non-zero,
    // else the sum of the per-size cells. This is what makes "ordered-only" articles (whose qty
    // lives in art_best and isn't broken down per size) still produce a row + subrow.
    for (const a of articles.values()) {
        a.totals = {};
        for (const m of measures) {
            const artV = a.hasArt[m.field] ? (a.artVal[m.field] || 0) : 0;
            a.totals[m.field] = artV || (a.cellSum[m.field] || 0);
        }
        a.hasDetail = Object.keys(a.cells).length > 0 || Object.values(a.totals).some(v => v);

        // Maatbalk signature = the article's WIDTH (X) size grid. Cells are re-keyed with it so
        // that subtotals/totals keep each maatbalk as its own size table. Length (Y) is excluded
        // on purpose: same-maatbalk articles that carry different lengths (e.g. only L30 vs only
        // L32) must still merge together — including Y here is what over-separated them before.
        const xs = [...a.xstruct.entries()].sort((p, q) => (Number(p[0]) || 0) - (Number(q[0]) || 0)).map(e => `${e[0]}:${e[1]}`);
        a.mbKey = xs.join(',');
        const mbCells = {};
        for (const k in a.cells) mbCells[`${a.mbKey}||${k}`] = a.cells[k];
        a.cells = mbCells;
    }

    const needle = (searchTerm || '').toLowerCase();
    const matches = (a) => !needle
        || descCols.some(d => String(a.desc[d.field] ?? '').toLowerCase().includes(needle))
        || a.groupLabels.some(l => String(l ?? '').toLowerCase().includes(needle));

    let list = [...articles.values()].filter(matches);
    // Order groups by their label (e.g. supplier name), and — because Array.sort is stable —
    // keep the original SQL ORDER BY sequence (levart1, barbody, kleurlev, kleurnr, …) WITHIN
    // each group. Re-sorting by the descriptive columns here is what made our order diverge
    // from the original FR3 report.
    list.sort((a, b) => {
        for (let i = 0; i < groups.length; i++) {
            const al = a.groupLabels[i] ?? '', bl = b.groupLabels[i] ?? '';
            if (al < bl) return -1;
            if (al > bl) return 1;
        }
        return 0;
    });

    // Stable display order of maatbalks, so subtotal/total size tables appear consistently.
    const mbOrder = new Map();
    list.forEach(a => { if (!mbOrder.has(a.mbKey)) mbOrder.set(a.mbKey, mbOrder.size); });

    const sumInto = (target, src) => {
        for (const k in src) target[k] = (target[k] || 0) + src[k];
    };

    const out = [];
    let rid = 0;
    const depth = groups.length;
    const levelKey = groups.map(() => null);
    const levelTotals = groups.map(() => ({}));   // measure totals per open group
    const levelCells  = groups.map(() => ({}));   // size cells per open group
    let initialized = false;

    const emitGroupHeader = (level, rec) => {
        out.push({
            __id__: `hdr_${rid++}`, __type__: 'group_header', __depth__: level,
            __label__: rec.groupLabels[level] || rec.groupVals[level] || '—',
            __hasDetail__: false,
        });
    };
    const emitSubtotal = (level, label) => {
        if (!showSubtotals) return; // only reports that actually carry subtotals ("tussentotalen")
        out.push({
            __id__: `sub_${rid++}`, __type__: 'subtotal', __depth__: level,
            __label__: label,
            __totals__: { ...levelTotals[level] },
            __cells__: { ...levelCells[level] },
            __hasDetail__: Object.keys(levelCells[level]).length > 0 || Object.values(levelTotals[level]).some(v => v),
        });
    };

    const grandTotals = {}, grandCells = {};

    for (const rec of list) {
        if (initialized) {
            let changedAt = depth;
            for (let i = 0; i < depth; i++) { if (rec.groupVals[i] !== levelKey[i]) { changedAt = i; break; } }
            for (let i = depth - 1; i >= changedAt; i--) {
                emitSubtotal(i, `${'Subtotaal'}: ${levelKey[i] != null && levelKey[i] !== '' ? (out.__lastLabel || levelKey[i]) : '—'}`);
                levelTotals[i] = {}; levelCells[i] = {}; levelKey[i] = rec.groupVals[i];
            }
            for (let i = changedAt; i < depth; i++) emitGroupHeader(i, rec);
        } else {
            for (let i = 0; i < depth; i++) { levelKey[i] = rec.groupVals[i]; emitGroupHeader(i, rec); }
            initialized = true;
        }

        // article (data) row
        out.push({
            __id__: `art_${rid++}`, __type__: 'data',
            ...rec.desc,
            __totals__: { ...rec.totals },
            __cells__: { ...rec.cells },
            __hasDetail__: rec.hasDetail,
            __groupLabels__: rec.groupLabels,
        });

        for (let i = 0; i < depth; i++) { sumInto(levelTotals[i], rec.totals); sumInto(levelCells[i], rec.cells); }
        sumInto(grandTotals, rec.totals); sumInto(grandCells, rec.cells);
    }
    if (initialized) {
        for (let i = depth - 1; i >= 0; i--) {
            emitSubtotal(i, `Subtotaal`);
        }
    }
    // grand total — only on reports that carry totals ("tussentotalen"), like the subtotals.
    if (showSubtotals && list.length) {
        out.push({
            __id__: 'grand_total', __type__: 'grand_total', __depth__: 0,
            __label__: 'Totaal',
            __totals__: grandTotals, __cells__: grandCells,
            __hasDetail__: Object.keys(grandCells).length > 0 || Object.values(grandTotals).some(v => v),
        });
    }

    return { displayRows: out, posX: sizeMap, posY: yMap, globalSizes, globalHasY, mbOrder, measures, descCols, groups };
};

// Build a nicer subtotal label that includes the group's name (resolved from the last header).
const fixSubtotalLabels = (rows, tSub) => {
    // Walk rows; remember the most recent group header label per depth, attach to following subtotal.
    const lastLabelByDepth = {};
    for (const r of rows) {
        if (r.__type__ === 'group_header') lastLabelByDepth[r.__depth__] = r.__label__;
        else if (r.__type__ === 'subtotal') r.__label__ = `${tSub}: ${lastLabelByDepth[r.__depth__] ?? ''}`.trim();
    }
    return rows;
};

const pctForBase = (base, lineSum, bestSum, geleSum) => {
    if (base === 'gele') return bestSum ? lineSum / bestSum : null;
    if (base === 'verk' || base === 'sver' || base === 'voor') return geleSum ? lineSum / geleSum : null;
    return null;
};
const fmtPctVal = (p) => (p === null || p === undefined) ? '' : (p * 100).toLocaleString('fr-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';

const SizeTablePanel = ({ row, posX, posY, mbOrder, measures, t }) => {
    const tx = (k, f) => { const v = t ? t(k) : k; return (!v || v === k) ? f : v; };
    const cells = row.__cells__ || {};
    const totalHdr = tx('reports_size_total', 'Totaal');
    const pctHdr = tx('reports_size_pct', 'Pct');
    const totals = row.__totals__ || {};

    // Cell keys are `${maatbalk}||${y}||${field}||${size}`. Each maatbalk gets its OWN size table
    // (its own size columns and length/Y axis) — so subtotal/total rows that span several
    // maatbalks render one clean table per maatbalk instead of a fused one.
    const entries = Object.entries(cells).map(([k, v]) => {
        const p = k.split('||');
        return { mb: p[0] || '', y: p[1] || '', field: p[2], size: p[3] || '', v };
    });
    const mbs = [...new Set(entries.map(e => e.mb))]
        .sort((a, b) => ((mbOrder && mbOrder.get(a)) ?? 9999) - ((mbOrder && mbOrder.get(b)) ?? 9999));

    const seenGlobal = new Set();
    const blocks = mbs.map(mb => {
        const mbEntries = entries.filter(e => e.mb === mb);
        const sizes = [...new Set(mbEntries.filter(e => e.size !== '').map(e => e.size))]
            .sort((a, b) => (posX.get(a) ?? 0) - (posX.get(b) ?? 0));
        const nonEmptyY = [...new Set(mbEntries.map(e => e.y).filter(y => y !== ''))]
            .sort((a, b) => (posY.get(a) ?? 0) - (posY.get(b) ?? 0));
        const hasEmptyY = mbEntries.some(e => e.y === '');
        const yList = [...nonEmptyY, ...(hasEmptyY ? [''] : [])];
        if (yList.length === 0) yList.push('');
        const sumFor = (y, base) => {
            const mm = measures.find(x => x.base === base);
            if (!mm) return 0;
            return mbEntries.filter(e => e.y === y && e.field === mm.field).reduce((s, e) => s + e.v, 0);
        };
        const detailRows = [];
        yList.forEach(y => {
            const bestSum = sumFor(y, 'best');
            const geleSum = sumFor(y, 'gele');
            measures.forEach(m => {
                const le = mbEntries.filter(e => e.y === y && e.field === m.field);
                const lineSum = le.reduce((s, e) => s + e.v, 0);
                if (lineSum === 0) return;
                seenGlobal.add(m.base);
                const vals = sizes.map(sz => { const e = le.find(x => x.size === sz); return e ? e.v : 0; });
                detailRows.push({ y, label: m.label, vals, total: lineSum, pct: fmtPctVal(pctForBase(m.base, lineSum, bestSum, geleSum)) });
            });
        });
        return { mb, sizes, yList, detailRows };
    }).filter(b => b.detailRows.length > 0);

    // Fallback: measures that only exist as an article-level total (no per-size cells anywhere).
    const fallbackRows = [];
    measures.forEach(m => {
        if (seenGlobal.has(m.base)) return;
        const tot = totals[m.field] || 0;
        if (tot) fallbackRows.push({ label: m.label, total: tot });
    });

    if (blocks.length === 0 && fallbackRows.length === 0) return null;

    const tableSx = { width: 'auto', '& td, & th': { py: 0.25, px: 1, borderColor: 'divider' } };

    return (
        <Box sx={{ p: 1, pl: 6, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {blocks.map((b, bi) => (
                <Table key={bi} size="small" sx={tableSx}>
                    <TableBody>
                        {b.yList.map((y) => {
                            const rowsForY = b.detailRows.filter(d => d.y === y);
                            if (!rowsForY.length) return null;
                            // The Y-size description sits in the corner (above the measure labels);
                            // blank when this maatbalk has no Y-size.
                            return (
                                <React.Fragment key={y || '_'}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700, ...FIRST_COL_SX }}>{y || ''}</TableCell>
                                        {b.sizes.map(sz => <TableCell key={sz} align="right" sx={{ fontWeight: 700 }}>{sz}</TableCell>)}
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>{totalHdr}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>{pctHdr}</TableCell>
                                    </TableRow>
                                    {rowsForY.map((dr, i) => (
                                        <TableRow key={i}>
                                            <TableCell sx={{ fontStyle: 'italic', ...FIRST_COL_SX }}>{dr.label}</TableCell>
                                            {dr.vals.map((v, j) => <TableCell key={j} align="right">{fmtInt(v)}</TableCell>)}
                                            <TableCell align="right" sx={{ fontWeight: 600 }}>{fmtInt(dr.total)}</TableCell>
                                            <TableCell align="right" sx={{ color: 'text.secondary' }}>{dr.pct}</TableCell>
                                        </TableRow>
                                    ))}
                                </React.Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            ))}
            {fallbackRows.length > 0 && (
                <Table size="small" sx={tableSx}>
                    <TableBody>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 700, ...FIRST_COL_SX }} />
                            <TableCell align="right" sx={{ fontWeight: 700 }}>{totalHdr}</TableCell>
                        </TableRow>
                        {fallbackRows.map((dr, i) => (
                            <TableRow key={i}>
                                <TableCell sx={{ fontStyle: 'italic', ...FIRST_COL_SX }}>{dr.label}</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>{fmtInt(dr.total)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
        </Box>
    );
};

const SizeMatrixReportGrid = (props) => {
    const { columns = [], rows = [], layout = null, showSubtotals = false, t, theme, reportTitle, rowCount, truncated } = props;
    const [searchTerm, setSearchTerm] = useState('');

    const cfg = useMemo(() => buildConfig(columns, layout, t), [columns, layout, t]);
    const model = useMemo(() => {
        const m = buildModel(columns, rows, cfg, searchTerm, showSubtotals);
        fixSubtotalLabels(m.displayRows, (t && t('reports_groupTotal')) || 'Subtotaal');
        return m;
    }, [columns, rows, cfg, searchTerm, showSubtotals, t]);

    const { displayRows, posX, posY, globalSizes, globalHasY, mbOrder, measures, descCols } = model;

    // Expandable detail panels — open by default; user can collapse/expand all.
    // MUI X v8 expects `detailPanelExpandedRowIds` to be a Set (it calls .has() internally),
    // so this must be a Set, not an array.
    const detailIds = useMemo(() => new Set(displayRows.filter(r => r.__hasDetail__).map(r => r.__id__)), [displayRows]);
    const [expandedIds, setExpandedIds] = useState(detailIds);
    useEffect(() => { setExpandedIds(detailIds); }, [detailIds]);

    const gridColumns = useMemo(() => {
        const cols = [];
        // Detail-panel toggle, but only on rows that actually have a subrow
        // (group-header bars have no detail, so no "+" there).
        cols.push({
            ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
            renderCell: (params) => (params.row.__hasDetail__ ? <GridDetailPanelToggleCell {...params} /> : null),
        });
        // Quantities (voor/best/gele/…) are intentionally NOT shown on the main row — they live
        // only in the size-table subrow, matching the original report.
        descCols.forEach((d, idx) => cols.push({
            field: d.field,
            headerName: d.label,
            minWidth: idx === 0 ? 150 : (d.money ? 78 : 110),
            flex: d.money ? 0 : 1,
            align: d.money ? 'right' : 'left',
            headerAlign: d.money ? 'right' : 'left',
            sortable: false,
            valueGetter: (_v, r) => (r?.__type__ === 'data' ? r?.[d.field] : (idx === 0 ? r?.__label__ : '')),
            renderCell: (p) => {
                if (p.row.__type__ !== 'data') return idx === 0 ? <strong>{p.row.__label__ ?? ''}</strong> : '';
                const val = p.row[d.field];
                if (d.money) { const n = num(val); return n ? '€ ' + n.toLocaleString('fr-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''; }
                return <span>{val ?? ''}</span>;
            },
        }));
        return cols;
    }, [descCols]);

    const exportFilename = (reportTitle || 'report')
        .replace(/[^a-z0-9-_]+/gi, '_').replace(/^_+|_+$/g, '').slice(0, 60) || 'report';

    // Export = the full matrix (one row per displayed row × measure, sizes as columns).
    const { exportColumns, exportRows, exportNumeric } = useMemo(() => {
        const tx = (k, f) => { const v = t ? t(k) : k; return (!v || v === k) ? f : v; };
        const totalHdr = tx('reports_size_total', 'Totaal');
        const yHdr = tx('reports_size_sizeY', 'Maat');
        const typeHdr = tx('reports_size_type', 'Type');
        const pctHdr = tx('reports_size_pct', 'Pct');
        const labelHdr = descCols[0] ? descCols[0].label : 'Naam';
        // Export uses the union of all sizes as columns (each row fills only its own maatbalk).
        const cols = [labelHdr, ...descCols.slice(1).map(d => d.label), ...(globalHasY ? [yHdr] : []), typeHdr, ...globalSizes, totalHdr, pctHdr];
        const pctFor = (base, lineSum, bestSum, geleSum) => {
            if (base === 'gele') return bestSum ? lineSum / bestSum : null;
            if (base === 'verk' || base === 'sver' || base === 'voor') return geleSum ? lineSum / geleSum : null;
            return null;
        };
        const fmtPct = (p) => (p === null || p === undefined) ? '' : (p * 100).toLocaleString('fr-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';
        const out = [];
        displayRows.forEach(r => {
            if (r.__type__ === 'group_header') return;
            const cells = r.__cells__ || {};
            const entries = Object.entries(cells).map(([k, v]) => { const p = k.split('||'); return { mb: p[0] || '', y: p[1] || '', field: p[2], size: p[3] || '', v }; });
            const baseLabel = r.__type__ === 'data' ? (r[descCols[0]?.field] ?? '') : (r.__label__ ?? '');
            const mbsR = [...new Set(entries.map(e => e.mb))];
            const sumFor = (mb, y, base) => { const mm = measures.find(x => x.base === base); if (!mm) return 0; return entries.filter(e => e.mb === mb && e.y === y && e.field === mm.field).reduce((s, e) => s + e.v, 0); };
            mbsR.forEach(mb => {
                const mbEntries = entries.filter(e => e.mb === mb);
                const ysForRow = [...new Set(mbEntries.map(e => e.y))];
                if (ysForRow.length === 0) ysForRow.push('');
                ysForRow.forEach(y => {
                    const bestSum = sumFor(mb, y, 'best');
                    const geleSum = sumFor(mb, y, 'gele');
                    measures.forEach(m => {
                        const lineEntries = mbEntries.filter(e => e.y === y && e.field === m.field);
                        const lineSum = lineEntries.reduce((s, e) => s + e.v, 0);
                        if (lineSum === 0) return;
                        const o = {};
                        o[labelHdr] = baseLabel;
                        descCols.slice(1).forEach(d => { o[d.label] = r.__type__ === 'data' ? (r[d.field] ?? '') : ''; });
                        if (globalHasY) o[yHdr] = y;
                        o[typeHdr] = m.label;
                        globalSizes.forEach(sz => { const e = lineEntries.find(x => x.size === sz); o[sz] = e ? e.v : ''; });
                        o[totalHdr] = lineSum;
                        o[pctHdr] = fmtPct(pctFor(m.base, lineSum, bestSum, geleSum));
                        out.push(o);
                    });
                });
            });
        });
        return { exportColumns: cols, exportRows: out, exportNumeric: new Set([...globalSizes, totalHdr]) };
    }, [displayRows, globalSizes, globalHasY, measures, descCols, t]);

    const articleCount = displayRows.filter(r => r.__type__ === 'data').length;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 400, gap: 1.5 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
                <TextField
                    size="small"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={t('reports_searchResults') || 'Search results…'}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
                    sx={{ minWidth: 240, flex: 1 }}
                />
                <Button size="small" startIcon={<UnfoldMoreIcon />} onClick={() => setExpandedIds(new Set(detailIds))}>
                    {t('reports_expandAll') || 'Expand all'}
                </Button>
                <Button size="small" startIcon={<UnfoldLessIcon />} onClick={() => setExpandedIds(new Set())}>
                    {t('reports_collapseAll') || 'Collapse all'}
                </Button>
                <Box sx={{ flex: 1 }} />
                <Tooltip title={t('reports_exportCsv') || 'Export CSV'}>
                    <span>
                        <Button size="small" startIcon={<DownloadIcon />}
                            onClick={() => exportCsv(exportColumns, exportRows, exportFilename)}
                            disabled={exportRows.length === 0}>CSV</Button>
                    </span>
                </Tooltip>
                <Tooltip title={t('reports_exportXlsx') || 'Export XLSX'}>
                    <span>
                        <Button size="small" startIcon={<TableViewIcon />}
                            onClick={() => exportXlsx(exportColumns, exportRows, exportFilename, { numericColumns: exportNumeric })}
                            disabled={exportRows.length === 0}>XLSX</Button>
                    </span>
                </Tooltip>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
                <Chip size="small" label={`${t('reports_size_articles') || 'Articles'}: ${articleCount}`} />
                <Chip size="small" variant="outlined" label={`${t('reports_rowCount') || 'Rows'}: ${rowCount ?? rows.length}`} />
                {truncated && <Chip size="small" color="warning" label={t('reports_truncated') || 'Result truncated to 50 000 rows'} />}
            </Stack>

            <Box sx={{ flex: 1, minHeight: 300 }}>
                <DataGridPro
                    className={theme}
                    rows={displayRows}
                    columns={gridColumns}
                    getRowId={(row) => row.__id__}
                    getDetailPanelContent={({ row }) =>
                        row.__hasDetail__
                            ? <SizeTablePanel row={row} posX={posX} posY={posY} mbOrder={mbOrder} measures={measures} t={t} />
                            : null
                    }
                    getDetailPanelHeight={() => 'auto'}
                    detailPanelExpandedRowIds={expandedIds}
                    onDetailPanelExpandedRowIdsChange={(ids) => setExpandedIds(ids)}
                    getRowClassName={(params) => {
                        if (params.row.__type__ === 'grand_total') return 'reports-grand-total-row';
                        if (params.row.__type__ === 'subtotal') return `reports-subtotal-row reports-subtotal-row-depth-${params.row.__depth__ || 0}`;
                        if (params.row.__type__ === 'group_header') return `reports-group-header-row reports-group-header-row-depth-${params.row.__depth__ || 0}`;
                        return '';
                    }}
                    density="compact"
                    disableRowSelectionOnClick
                    pagination
                    pageSizeOptions={[50, 100, 250, 500]}
                    initialState={{ pagination: { paginationModel: { pageSize: 100 } } }}
                    sx={{
                        '& .reports-group-header-row':         { backgroundColor: 'rgba(34, 197, 94, 0.18)', fontWeight: 700 },
                        '& .reports-group-header-row-depth-1': { backgroundColor: 'rgba(34, 197, 94, 0.12)' },
                        '& .reports-subtotal-row':             { backgroundColor: 'rgba(99, 102, 241, 0.14)', fontWeight: 700 },
                        '& .reports-grand-total-row':          { backgroundColor: 'rgba(239, 68, 68, 0.10)', fontWeight: 700 },
                    }}
                />
            </Box>
        </Box>
    );
};

export default SizeMatrixReportGrid;
